import * as fs from "node:fs";
import * as path from "node:path";
import { LocalCliError } from "./errors.js";
import { resolvePlatformRepoRoot } from "./brain-paths.js";
import { agentContentHint } from "./agent-content-sync.js";
import { deriveOwnedPaths, buildSeedManifest, serializeSeedManifest, SEED_MANIFEST_PATH, type SeedUpstream } from "./seed-manifest.js";

/**
 * THE seed-source seam. Resolves a seed NAME to a directory of overlay content.
 *
 * Always `brain-seeds/<name>/` under the repo root — and deliberately still so.
 * The graduation this comment used to anticipate (fetch a remote seed here) landed
 * one layer up instead: `m8t agent-content sync` materializes a pinned agent repo
 * INTO `brain-seeds/<name>/`, so callers and this function stay agnostic to
 * whether a seed is committed here or generated from an agent repo. Resolving
 * remotely from inside this function would have put a network fetch behind every
 * seed lookup, including the install path, for no gain.
 *
 * See docs/architecture/AGENT-REPO-CONTRACT.md.
 *
 * Throws LocalCliError(SEED_NOT_FOUND) when the named seed dir is absent.
 */
export function resolveSeed(name: string): string {
  const root = resolvePlatformRepoRoot("Seeding a brain", ["brain-seeds"]);
  const dir = path.join(root, "brain-seeds", name);
  if (!fs.existsSync(dir)) {
    throw new LocalCliError({
      code: "SEED_NOT_FOUND",
      message: `--seed "${name}": no brain-seeds/${name}/ directory found.`,
      // A product agent's seed is generated from its pinned repo rather than
      // committed here, so "check the name" is the wrong advice for the most
      // likely cause — an un-hydrated checkout.
      hint:
        agentContentHint(root, name) ??
        "Seeds live under brain-seeds/. Check the name, or omit --seed for an unseeded brain.",
    });
  }
  return dir;
}

/**
 * Materialize a brain repo tree into destDir: copy the structural template,
 * then (if a seed is given) overlay the seed's content on top — seed wins on
 * every collision (fs.cpSync force:true; no merge). With no seedDir, the body
 * is exactly today's single cpSync → byte-identical no-seed guarantee.
 */
export function materializeBrainTree(opts: {
  templateSrc: string;
  destDir: string;
  seedDir?: string;
}): void {
  fs.cpSync(opts.templateSrc, opts.destDir, { recursive: true });
  if (opts.seedDir) {
    fs.cpSync(opts.seedDir, opts.destDir, { recursive: true });
  }
}

/**
 * Write the initial `.m8t/seed-manifest.json` so a newly-scaffolded brain repo
 * carries the marker from day one (no bootstrap needed on its first refresh).
 * `ownedPaths` is derived structurally from the seed dir's SEED_PATHS subtrees
 * (via deriveOwnedPaths) — it can never include memory/ or other non-seed content.
 */
export function writeSeedMarker(args: {
  destDir: string;
  seedName: string;
  seedDir: string;
  refreshedToCommit: string;
  version: string;
  /** The agent repo + pinned ref the seed content came from, when it came from one.
   *  Omitted for a repo-local seed — and absent markers stay valid forever. */
  upstream?: SeedUpstream;
}): void {
  const manifest = buildSeedManifest([{
    name: args.seedName,
    refreshedToCommit: args.refreshedToCommit,
    refreshedToVersion: args.version,
    ownedPaths: deriveOwnedPaths(args.seedDir),
    ...(args.upstream ? { upstream: args.upstream } : {}),
  }]);
  const dest = path.join(args.destDir, SEED_MANIFEST_PATH);
  fs.mkdirSync(path.dirname(dest), { recursive: true });
  fs.writeFileSync(dest, serializeSeedManifest(manifest));
}
